import type { IImageForUpload } from 'src/types/image';
import type { IUser, IUserProfile } from 'src/types/user';

import useSWR, { mutate } from 'swr';
import { useMemo, useState, useCallback } from 'react';

import axios, { updater, fetcher, getEndpoints, updateEndpoints } from 'src/utils/axios';

import { useAuthContext } from 'src/auth/hooks';

import { appendImageToFormData } from 'src/types/image';

// ----------------------------------------------------------------------

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 0, // Disable automatic re-fetching
};

export function useGetAndUpdateUserContext({ userId }: { userId?: number } = {}) {
  const { getUser, user, isLoading, error } = useGetUser({ userId });
  const { setUserProfile } = useAuthContext();

  const getUserAndUpdateContext = useCallback(
    async ({ authId }: { authId?: string } = {}) => {
      const userData = await getUser({ authId });

      if (userId) {
        mutate(getEndpoints.user.byId(userId), undefined, {
          revalidate: true,
        });
      }

      if (userData) {
        setUserProfile(userData);
      }
      return userData;
    },
    [getUser, setUserProfile, userId]
  );

  return { getUserAndUpdateContext, user, isLoading, error };
}

export function useGetUser({ userId }: { userId?: number } = {}) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<IUser | null>(null);

  const getUser = useCallback(
    async ({ authId }: { authId?: string } = {}) => {
      if (!authId && !userId) {
        throw new Error('Either authId or userId must be provided');
      }

      setIsLoading(true);
      setError(null);

      try {
        let response;
        if (authId) {
          response = await axios.get(getEndpoints.user.byAuthId(authId));
        } else if (userId) {
          response = await axios.get(getEndpoints.user.byId(userId));
        }

        // Map API response to IUser interface
        const apiData = response?.data.data;
        const userData: IUser = {
          id: apiData.ID, // Map uppercase ID to lowercase id
          ...apiData,
        };

        setUser(userData);
        return userData;
      } catch (err: any) {
        console.error('Error fetching user profile:', err);
        setError(err.message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  const memoizedValue = useMemo(
    () => ({
      getUser,
      user,
      isLoading,
      error,
    }),
    [getUser, user, isLoading, error]
  );

  return memoizedValue;
}

export function useGetUserProfile(guid: string) {

  const url = guid ? getEndpoints.user.byGuid(guid) : null;
  const { data, isLoading, error, isValidating } = useSWR<{ data: IUserProfile }>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      userProfile: data?.data,
      userProfileLoading: isLoading,
      userProfileError: error,
      userProfileValidating: isValidating,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue
}

//---------------------------------------------------------------------------
// UPDATE PLACE PARAMS
//
//
//---------------------------------------------------------------------------

// takes the IUser object. Removes guid and email from updates and says all other vars are optional.
// Then adds the guid back in as a requirement
type PartialUserUpdate = Partial<Omit<IUser, 'guid' | 'email'>>;

export function useUpdateUserProfile(guid: string) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userProfile, setUserProfile } = useAuthContext();

  const updateUserProfile = useCallback(
    async ({ displayName, tagline, bio, webURL }: PartialUserUpdate) => {
      setIsUpdating(true);
      setError(null);

      try {
        const formData = new FormData();
        if (displayName) formData.append('displayName', displayName);
        if (tagline) formData.append('tagline', tagline);
        if (bio) formData.append('bio', bio);
        if (webURL) formData.append('webURL', webURL);

        // Send the form data to the appropriate endpoint
        const result = await mutate(
          updateEndpoints.user.user(guid),
          updater(updateEndpoints.user.user(guid), formData)
        );
        if (result.error) {
          throw result.error;
        }

        await mutate(updateEndpoints.user.user(guid));
        const updatedUserProfile = result.data as IUserProfile;
        setUserProfile({
          ...updatedUserProfile,
          image: userProfile?.image,
        });

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [guid, setUserProfile, userProfile]
  );

  const memoizedValue = useMemo(
    () => ({
      updateUserProfile,
      isUpdating,
      error,
    }),
    [updateUserProfile, isUpdating, error]
  );

  return memoizedValue;
}

export function useCreateUserProfileImage(guid: string) {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userProfile, setUserProfile } = useAuthContext();

  const createUserProfileImage = useCallback(
    async (imageForUpload: IImageForUpload) => {
      setIsCreating(true);
      setError(null);

      try {
        const formData = new FormData();
        appendImageToFormData(formData, imageForUpload, 'imageReq');

        // Send the form data to the appropriate endpoint
        const result = await mutate(
          updateEndpoints.user.user(guid),
          updater(updateEndpoints.user.user(guid), formData)
        );
        if (result.error) {
          throw result.error;
        }

        await mutate(updateEndpoints.user.user(guid), undefined, { revalidate: true });
        await mutate(
          (key) => typeof key === 'string' && key.startsWith('/user/'),
          undefined,
          { revalidate: true }
        );

        const updatedUserProfile = result.data as IUserProfile;

        setUserProfile({
          ...userProfile!,
          image: updatedUserProfile.image,
        });

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [guid, setUserProfile, userProfile]
  );

  const memoizedValue = useMemo(
    () => ({
      createUserProfileImage,
      isCreating,
      error,
    }),
    [createUserProfileImage, isCreating, error]
  );

  return memoizedValue;
}
